import PropTypes from 'prop-types';
import React from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { layoutTypes } from "./constants/layout";
// Import Routes all
import { authProtectedRoutes, publicRoutes, actorProtectedRoutes, actorPublicRoutes, authEditorRoutes } from "./routes";
import { Bounce, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import all middleware

// layouts Format
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

// Import scss
import "./assets/scss/theme.scss";
import './App.css';

// Import Firebase Configuration file
// import { initFirebaseBackend } from "./helpers/firebase_helper";

import fakeBackend from "./helpers/AuthType/fakeBackend";
import { ActorAuthmiddleware, ActorPublicAuthMiddleware, Authmiddleware } from './routes/route';
import ErrorBoundary from "./components/ErrorBoundary";
import { getActiveAccountId, buildScopedPath, getStoredAccountId } from "./utils/account-context";
import { hasActiveUserSession } from "./utils/auth-helpers";
import { AccountBrandingProvider } from "./context/account-branding-context";
import { DesignSystemProvider } from "@trustvid/design-system";

// Activating fake backend
fakeBackend();

// const firebaseConfig = {
//   apiKey: process.env.REACT_APP_APIKEY,
//   authDomain: process.env.REACT_APP_AUTHDOMAIN,
//   databaseURL: process.env.REACT_APP_DATABASEURL,
//   projectId: process.env.REACT_APP_PROJECTID,
//   storageBucket: process.env.REACT_APP_STORAGEBUCKET,
//   messagingSenderId: process.env.REACT_APP_MESSAGINGSENDERID,
//   appId: process.env.REACT_APP_APPID,
//   measurementId: process.env.REACT_APP_MEASUREMENTID,
// };

// init firebase backend
// initFirebaseBackend(firebaseConfig);


const getLayout = (layoutType) => {
  let Layout = VerticalLayout;
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout;
      break;
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout;
      break;
    default:
      break;
  }
  return Layout;
};

const FallbackRoute = () => {
  const location = useLocation();
  const userType = localStorage.getItem("userType");

  if (userType === "actor") {
    return <Navigate to="/login" replace />;
  }

  // Never bounce unknown paths to dashboard without a valid session (stale accountId alone is not enough).
  if (!hasActiveUserSession()) {
    return <Navigate to="/login" replace />;
  }

  // Unknown paths (e.g. old /docs/*.md links) must not treat the first segment as an account id.
  const accountId = getStoredAccountId() || getActiveAccountId(location.pathname);
  if (!accountId) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={buildScopedPath(accountId, "/dashboard")} replace />;
};

const ScopedLayoutWrapper = ({ LayoutComponent, children }) => {
  const location = useLocation();
  const accountId = getActiveAccountId(location.pathname) || "global";
  return <LayoutComponent key={`layout-${accountId}`}>{children}</LayoutComponent>;
};

const App = () => {
  const location = useLocation();

  const selectLayoutState = (state) => state.Layout;
  const LayoutProperties = createSelector(
    selectLayoutState,
    (layout) => ({
      layoutType: layout.layoutType,
    })
  );

  const {
    layoutType
  } = useSelector(LayoutProperties);

  const Layout = getLayout(layoutType);

  return (
   <ErrorBoundary resetKey={location.pathname}>
    <DesignSystemProvider>
    <AccountBrandingProvider>
    <React.Fragment>
      <Routes>
        {actorPublicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <ActorPublicAuthMiddleware>
                {route.component}
              </ActorPublicAuthMiddleware>
            }
            key={idx}
            exact={true}
          />
        ))}
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <NonAuthLayout>
                {route.component}
              </NonAuthLayout>
            }
            key={idx}
            exact={true}
          />
        ))}
        {actorProtectedRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <ActorAuthmiddleware roles={route.roles}>
                {route.component}
              </ActorAuthmiddleware>
            }
            key={idx}
            exact={true}
          />
        ))}
        {authProtectedRoutes.map((route, idx) => (
          <Route
            key={idx}
            path={route.path}
            element={
              <Authmiddleware roles={route.roles}>
                {
                  route?.hidelayout ?
                    route.component
                  : (
                    <ScopedLayoutWrapper LayoutComponent={Layout}>
                      {route.component}
                    </ScopedLayoutWrapper>
                  )
                }
                
              </Authmiddleware>
            }
          />
        ))}

        {/** Auth Editor Route */} 
        {
          authEditorRoutes.map((route, idx) => (
            <Route
              key={idx}
              path={route.path}
              element={
                <Authmiddleware roles={route.roles}>
                  {route.component}
                </Authmiddleware>
              }
            />
          ))
        }

        <Route path="*" element={<FallbackRoute />} />

      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </React.Fragment>
    </AccountBrandingProvider>
    </DesignSystemProvider>
     </ErrorBoundary>
  );
};

App.propTypes = {
  layout: PropTypes.any
};

ScopedLayoutWrapper.propTypes = {
  LayoutComponent: PropTypes.elementType.isRequired,
  children: PropTypes.any,
};

export default App;